import i18n from "../../../i18n";
import { Trans } from "react-i18next";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ChevronRight, ScrollText, BarChart3, Users } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { PageHeader, LoadingState, EmptyState } from "../../../components/ui";
import {
  fetchMyPolls,
  fetchPollById,
  effectiveStatus,
  getCurrentUser,
} from "../../../services/pollServices";
import { TablePagination } from "../../leave-management/components/TablePagination";
import { CreatePollModal } from "../components/CreatePollModal";
import { StatusPill, ResponseTypePill } from "../components/StatusPill";
import { Toast } from "../components/Toast";
import {
  ROUTES,
  RESPONSE_TYPE_COLOR,
  formatDateTime,
  describeAudience,
} from "../constants/pollOptions";

const PAGE_SIZE = 10;

// Drafts first — they are the only cards that still need an action from the
// creator — then live polls, then the finished ones.
const STATUS_ORDER = { Draft: 0, Active: 1, Closed: 2 };

function PollCard({ poll, onEdit, onOpenResults }) {
  const status = effectiveStatus(poll);
  const barColor = RESPONSE_TYPE_COLOR[poll.responseType] ?? "var(--color-accent)";
  const published = status === "Active" || status === "Closed";

  return (
    <div
      className="bg-white rounded-xl px-6 py-5 border-l-4 shadow-[0_1px_3px_rgba(0,0,0,0.05)] hover:shadow-[0_4px_14px_rgba(30,51,131,0.12)] hover:-translate-y-px transition"
      style={{ borderLeftColor: barColor }}>

      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h3 className="text-[18px] font-medium text-foreground break-words">{poll.pollTitle}</h3>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)] break-words">{poll.pollQuestion}</p>
        </div>
        <StatusPill status={status} />
      </div>

      <div className="flex items-center gap-2.5 mt-3 text-[13px] text-foreground flex-wrap">
        <ResponseTypePill responseType={poll.responseType} />
        <span className="pl-2.5 border-l border-[var(--color-text-border)] inline-flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5 text-[var(--color-text-secondary)]" />
          {describeAudience(poll.audience)}
        </span>
        {poll.endDateTime && (
          <span className="pl-2.5 border-l border-[var(--color-text-border)] text-[var(--color-text-secondary)]">
            {status === "Closed" ? "Closed" : "Closes"} {formatDateTime(poll.endDateTime)}
          </span>
        )}
        {poll.anonymous && (
          <span className="pl-2.5 border-l border-[var(--color-text-border)] text-[var(--color-text-secondary)]">
            <Trans i18nKey="polls.anonymous" />
          </span>
        )}
      </div>

      {status === "Draft" && (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => onEdit(poll)}
            className="bg-[var(--color-primary)] text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-[var(--color-accent)] min-h-[40px]">
            <Trans i18nKey="formActions.continueDraft" />
          </button>
        </div>
      )}

      {published && (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => onOpenResults(poll)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-text-border)] bg-white px-4 py-2 text-sm font-semibold text-[var(--color-accent)] hover:bg-[var(--color-workspace)] min-h-[40px]">
            <BarChart3 className="h-4 w-4" /> <Trans i18nKey="formActions.viewResults" />
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Screen 3 — My Polls. The creator's landing screen: own polls by status.
 * HR additionally gets the full poll log from the header.
 */
export function MyPollsPage() {
  const navigate = useNavigate();
  const user = getCurrentUser();
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [page, setPage] = useState(1);

  const load = () => {
    setLoading(true);
    fetchMyPolls()
      .then(setPolls)
      .catch((err) => setError(err.message || "Failed to load polls."))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  // Sort is stable, so the server's ordering still decides within each group.
  const ordered = useMemo(
    () =>
      [...polls].sort(
        (a, b) =>
          (STATUS_ORDER[effectiveStatus(a)] ?? 99) - (STATUS_ORDER[effectiveStatus(b)] ?? 99)
      ),
    [polls]
  );

  const totalPages = Math.max(Math.ceil(ordered.length / PAGE_SIZE), 1);
  const safePage = Math.min(page, totalPages);
  const visible = ordered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleEdit = async (poll) => {
    try {
      const fresh = await fetchPollById(poll.pollId);
      setEditItem(fresh);
    } catch {
      setEditItem(poll);
    }
    setModalOpen(true);
  };

  return (
    <AppLayout>
      <PageBackLink to="/" />
      <PageHeader
        title={i18n.t("polls.myPolls")}
        action={
          <div className="flex items-center gap-2 flex-wrap">
            {user.isHR && (
              <button
                type="button"
                onClick={() => navigate(ROUTES.hrLog)}
                className="inline-flex items-center gap-2 h-11 px-5 rounded-xl border border-[var(--color-text-border)] bg-white text-[var(--color-primary)] font-semibold text-sm hover:bg-[var(--color-workspace)] transition cursor-pointer">
                <ScrollText className="h-4 w-4" /> <Trans i18nKey="polls.pollLog" />
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setEditItem(null);
                setModalOpen(true);
              }}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[var(--color-primary)] text-white font-semibold text-sm hover:bg-[var(--color-accent)] transition cursor-pointer">
              <Plus className="h-4 w-4" /> <Trans i18nKey="polls.newPoll" />
            </button>
          </div>
        } />

      <div className="max-w-[1050px] flex flex-col gap-4">
        {loading ? (
          <LoadingState message="Loading polls…" />
        ) : error ? (
          <p className="p-6 text-sm text-[var(--color-error)]">{error}</p>
        ) : ordered.length === 0 ? (
          <EmptyState
            icon={BarChart3}
            title={i18n.t("polls.noPollsYet")}
            message="Create a poll to gather a decision or a quick read from your team." />
        ) : (
          <>
            {visible.map((poll) => (
              <PollCard
                key={poll.pollId}
                poll={poll}
                onEdit={handleEdit}
                onOpenResults={(p) => navigate(ROUTES.creatorResults(p.pollId))} />
            ))}
            <div className="mt-4">
              <TablePagination
                page={safePage}
                totalPages={totalPages}
                totalItems={ordered.length}
                pageSize={PAGE_SIZE}
                onPageChange={setPage} />
            </div>
          </>
        )}
      </div>

      {modalOpen && (
        <CreatePollModal
          open={modalOpen}
          initialData={editItem}
          onClose={(refresh, message) => {
            setModalOpen(false);
            setEditItem(null);
            if (refresh) {
              load();
              if (message) setSuccessMsg(message);
            }
          }} />
      )}

      {successMsg && <Toast tone="success" onDone={() => setSuccessMsg("")}>✓ {successMsg}</Toast>}
    </AppLayout>
  );
}
